import React, {
  useState,
  useEffect,
  useContext,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { searchDocuments, exportDocuments, clearDocuments } from 'src/actions/documentActions';
import { useHistory, useLocation } from 'react-router-dom';
import * as QueryString from 'query-string';

import {
  Box, Button, Grid, IconButton, Tooltip
} from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import GetAppIcon from '@mui/icons-material/GetApp';

import moment from 'moment';
import { DashboardContext } from 'src/context/DashboardContext';
import SaveSearchDialog from 'src/views/documents/DashboardView/DocumentsSearchList/SaveSearchDialog';
import SearchTextField from './searchTextField';

const useStyles = makeStyles(() => ({
  containerFields: {
    display: 'flex',
    height: '100%',
    margin: 'auto'
  },

  container: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center'
  },

  item: {
    width: '100%',
    '& .MuiGrid-item': {
      padding: '8px'
    }
  },

  actionItem: {
    width: '100%',
    height: 58,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  actionItemSearch: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  btn: {
    color: 'white',
    marginLeft: 0,
    display: 'flex',
    flex: '1 1 auto',
    minHeight: '100%',
    '&:hover': {
      opacity: 0.9
    }
  },

  search_btn: {
    color: 'white',
    marginLeft: 0,
    display: 'flex',
    flex: '1 1 auto',
    minHeight: '100%',
    background: '#5850EC',
    '&:hover': {
      background: '#5850EC',
      opacity: 0.9
    }
  }
}));

const SearchFields = () => {
  const location = useLocation();
  const history = useHistory();

  const {
    setSearchPerformed,
    setActiveTab,
    docSearchFields,
    setDocSearchFields,
    activeTab,
    checkedDocuments
  } = useContext(DashboardContext);

  const classes = useStyles();

  const repo = useSelector((state) => state.repository.selected);
  const account = useSelector((state) => state.account);
  const resultCount = useSelector((state) => state.document.list.length);

  const dispatch = useDispatch();

  const [defaultComparator, setDefaultComparator] = useState();
  const [openSaveSearchDialog, setOpenSaveSearchDialog] = useState(false);
  const recycleTab = !!(activeTab === 2);
  const savedSearchTab = !!(activeTab === 1);
  const [filterButton, setFilterButton] = useState(false);
  const [filterTooltip, setFilterTooltip] = useState('More Fields');

  const queryFields = docSearchFields.filter((field) => (field.value && field.value.trim() !== '') || field.comparator === 'is empty' || field.comparator === 'previous 7 days'
    || field.comparator === 'previous 30 days' || field.comparator === 'not set');

  const handleSearch = async () => {
    let data = {
      repositoryId: repo.id,
      repositoryName: repo.name,
      queryFields,
      sortField: '',
      sortDirection: 'ASC',
      maxHits: 0,
      deleted: false
    };
    if (recycleTab) {
      data = {
        repositoryId: repo.id,
        repositoryName: repo.name,
        queryFields,
        sortField: '',
        sortDirection: 'ASC',
        maxHits: 0,
        deleted: true
      };
    }
    // Clear the existing searcxh, if any.
    dispatch(clearDocuments());

    // Perform the new list
    dispatch(searchDocuments(data));

    setSearchPerformed(true);

    if (savedSearchTab) {
      setActiveTab(0);
    }

    // Set appropriate landing layout
    if (QueryString.parse(location.search) && !location.pathname.endsWith('dashboard')) {
      history.push('/app/documents/list');
    }
  };

  const handleExport = async () => {
    const isChecked = checkedDocuments.length > 0;
    let data = {
      repositoryName: repo.name,
      queryFields,
      sortField: '',
      sortDirection: 'ASC',
      maxHits: 0,
      deleted: false
    };
    if (recycleTab) {
      data = {
        repositoryName: repo.name,
        queryFields,
        sortField: '',
        sortDirection: 'ASC',
        maxHits: 0,
        deleted: true
      };
    }
    if (isChecked) {
      data = checkedDocuments;
    }
    dispatch(exportDocuments(data, isChecked));
  };

  const handleSelect = (name, comparator) => {
    const index = docSearchFields.findIndex((field) => field.name === name);

    let value = '';
    const searchfield = docSearchFields[index];
    if (searchfield !== undefined && searchfield.value !== undefined) {
      value = searchfield.value;
      // We had a between query with two dates.  The new query is not a between and should
      // have only one date.
      if (comparator !== 'between' && searchfield.comparator === 'between') {
        const firstdate = value.split('~')[0];
        value = firstdate;
      }
    }
    if (index === -1) {
      setDocSearchFields([...docSearchFields, { name, value: '', comparator }]);
    } else {
      setDocSearchFields(
        docSearchFields.map((field) => (
          field.name === name ? { ...field, value, comparator } : field
        ))
      );
    }
  };

  const handleChange = (name, value) => {
    const index = docSearchFields.findIndex((field) => field.name === name);

    if (index === -1) {
      setDocSearchFields(
        [
          ...docSearchFields,
          { name, value, comparator: defaultComparator }
        ]
      );
    } else {
      setDocSearchFields(
        docSearchFields.map((field) => (
          field.name === name ? { ...field, value } : field
        ))
      );
    }
  };

  const handleChangeDate = (name, value1, value2) => {
    const date1 = value1 ? moment(value1).format('MM/DD/YYYY') : null;
    const date2 = value2 ? moment(value2).format('MM/DD/YYYY') : null;
    handleChange(name, date2 ? `${date1}~${date2}` : date1);
  };

  const handleChangeText = (name, value1, value2) => {
    const text1 = value1;
    const text2 = value2;
    handleChange(name, text2 ? `${text1}~${text2}` : text1);
  };

  const handleToggleFilterDisplay = () => {
    setFilterButton(!filterButton);
    if (!filterButton) {
      setFilterTooltip('Fewer Fields');
    } else {
      setFilterTooltip('More Fields');
    }
  };

  const handleAutoSearch = useCallback(async (params) => {
    const comparatorObj = {
      $is: 'is',
      $equals: 'equals',
      '$is not': 'is not',
      '$not equals': 'not equals',
      $gt: 'greater than',
      $after: 'after',
      '$greater than': 'greater than',
      $lt: 'less than',
      $before: 'before',
      '$less than': 'less than',
      $starts: 'starts with',
      '$starts with': 'starts with',
      $ends: 'ends with',
      '$ends with': 'ends with',
      $like: 'like',
      $contains: 'contains',
      '$contains all': 'contains all',
      '$is empty': 'is empty',
    };

    const { $r, $d, ...paramsCopy } = params;
    const queryFieldArray = Object.keys(paramsCopy).map((key) => {
      const splitParams = paramsCopy[`${key}`].split(':');

      const value = splitParams.length === 2
        ? splitParams[1]?.trim().toLowerCase()
        : splitParams[0]?.trim().toLowerCase();

      const comparator = splitParams.length === 2
        ? comparatorObj[splitParams[0]?.trim().toLowerCase()]
        : 'is';

      return {
        name: key,
        value,
        comparator: comparator || 'is'
      };
    });

    await setDocSearchFields(queryFieldArray);
    await setSearchPerformed(true);
    await setActiveTab(0);

    const data = {
      repositoryName: repo.name,
      queryFields: queryFieldArray,
      sortField: '',
      sortDirection: 'ASC',
      maxHits: 0,
      deleted: false
    };

    dispatch(searchDocuments(data));
  }, [dispatch, repo, setActiveTab, setDocSearchFields, setSearchPerformed]);

  useEffect(() => {
    const params = QueryString.parse(location.search);

    if (Object.keys(params).length && repo?.name) {
      handleAutoSearch(params);
    }
  }, [repo, handleAutoSearch, location.search, history, location.pathname]);

  const auditFalse = false;

  const displayCount = filterButton ? repo.searchFields.length : 4;
  const fieldValue = (searchfield, val) => (docSearchFields.find((field) => field.name === searchfield.name) ? docSearchFields.find((field) => field.name === searchfield.name)[`${val}`] : '');

  const renderFields = () => (
    repo.searchFields.map((searchfield, index) => (
      (index < displayCount) && (
        <Grid key={searchfield.name} item sm={3} className={classes.item}>
          <SearchTextField
            field={searchfield}
            handleChange={handleChange}
            handleChangeDate={handleChangeDate}
            handleChangeText={handleChangeText}
            handleSelect={handleSelect}
            setDefaultComparator={setDefaultComparator}
            value={fieldValue(searchfield, 'value')}
            comparator={fieldValue(searchfield, 'comparator')}
          />

        </Grid>
      )
    ))
  );

  return (
    <>
      <SaveSearchDialog
        open={openSaveSearchDialog}
        setOpen={setOpenSaveSearchDialog}
        fields={queryFields}
        audit={auditFalse}
      />
      <Grid
        container
      >
        <Grid item md={9} className={classes.item} style={{ display: 'flex', alignItems: 'start', }}>
          <form
            style={{ width: '100%' }}
            id="search-docs-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <Box style={{
              width: '100%', display: 'flex', alignItems: 'center', padding: 0, margin: 0
            }}
            >
              <Grid container spacing={2} className={classes.containerFields}>
                {
                repo && renderFields()
              }
              </Grid>
            </Box>
          </form>
        </Grid>
        <Grid item md={1}>
          <Box className={classes.actionItemSearch} style={{ height: 58, padding: 8 }}>
            {repo ? (
              <Button
                className={classes.search_btn}
                color="inherit"
                variant="contained"
                type="submit"
                form="search-docs-form"
              >
                Search
              </Button>
            ) : ''}
          </Box>
        </Grid>
        <Grid item md={2}>
          <Grid container>
            <Grid item md={3}>
              <Box className={classes.actionItem}>
                {repo && repo.searchFields.length > 4 ? (
                  <Tooltip title={filterTooltip}>
                    <IconButton className={classes.btn} onClick={handleToggleFilterDisplay} size="large">
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                ) : ''}
              </Box>
            </Grid>
            <Grid item md={3}>
              <Box className={classes.actionItem}>
                {docSearchFields.length ? (
                  <Tooltip title="Clear Fields">
                    <IconButton
                      className={classes.btn}
                      onClick={() => setDocSearchFields([])}
                      size="large"
                    >
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                ) : ''}
              </Box>
            </Grid>
            <Grid item md={3}>
              <Box className={classes.actionItem}>
                {resultCount ? (
                  <Tooltip title="Download Results">
                    <IconButton className={classes.btn} onClick={() => handleExport()} size="large">
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                ) : ''}
              </Box>
            </Grid>
            <Grid item md={3}>
              <Box className={classes.actionItem}>
                {!recycleTab && docSearchFields.length && repo && repo.id > 0
                  && !account.user?.isDisplayDocUser ? (
                    <Tooltip title="Save Search">
                      <IconButton
                        className={classes.btn}
                        onClick={() => setOpenSaveSearchDialog(true)}
                        size="large"
                      >
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                  ) : ''}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default SearchFields;
